import { Extension } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import { Plugin } from '@tiptap/pm/state';
import { Decoration, DecorationSet, type EditorView } from '@tiptap/pm/view';
import {
  investmentRowShowsHours,
  investmentTableShowsHours,
} from '../../../../backend/src/utils/investmentRowDisplay';

export const HIDDEN_HOURS_CLASS = 'investment-hours-hidden';
/** Alleen omschrijvingen in de tabel; het bedrag staat enkel in de totaalregel. */
export const HIDDEN_ROW_PRICES_CLASS = 'investment-row-prices-hidden';
const PRICE_ONLY_CLASS = 'investment-row-price-only';

function investmentTables(doc: PMNode) {
  const tables: Array<{
    node: PMNode;
    pos: number;
    hourly: boolean;
    showHours: boolean;
    defaultShowHours: boolean;
    showRowPrices: boolean;
  }> = [];
  doc.descendants((group, pos) => {
    if (group.type.name !== 'investmentGroupNode') return true;
    let pricingType = 'fixed';
    let defaultShowHours = true;
    let showRowPrices = true;
    group.forEach((child, offset) => {
      if (child.type.name === 'investmentHeaderNode' && !child.attrs.isMainHeader) {
        pricingType = child.attrs.pricingType;
        defaultShowHours = child.attrs.showHourColumns !== false;
        showRowPrices = child.attrs.showRowPrices !== false;
      }
      if (child.type.name !== 'table') return;
      const rows = Array.from({ length: child.childCount }, (_, index) =>
        child.child(index)
      ).filter((row) => row.firstChild?.type.name !== 'tableHeader');
      tables.push({
        node: child,
        pos: pos + 1 + offset,
        hourly: pricingType === 'hourly',
        defaultShowHours,
        showRowPrices,
        showHours: investmentTableShowsHours(
          rows.map((row) => row.attrs),
          pricingType,
          defaultShowHours
        ),
      });
    });
    return false;
  });
  return tables;
}

/** Of de fase waar de tabelregel op `pos` in staat bedragen per regel toont. */
export function investmentRowPricesVisible(doc: PMNode, pos: number): boolean {
  const $pos = doc.resolve(pos);
  for (let depth = $pos.depth; depth > 0; depth--) {
    const group = $pos.node(depth);
    if (group.type.name !== 'investmentGroupNode') continue;
    let visible = true;
    for (let index = 0; index <= $pos.index(depth); index++) {
      const child = group.child(index);
      if (child.type.name === 'investmentHeaderNode' && !child.attrs.isMainHeader)
        visible = child.attrs.showRowPrices !== false;
    }
    return visible;
  }
  return true;
}

function subtotalColumnWidth(table: PMNode) {
  const subtotalCell = table.firstChild?.childCount === 4 ? table.firstChild.child(3) : null;
  const width = Number(subtotalCell?.attrs.colwidth?.[0]) || 140;
  return `${Math.max(72, Math.round(width))}px`;
}

function tableAttributes(table: PMNode) {
  return {
    class: HIDDEN_HOURS_CLASS,
    style: `--investment-subtotal-width:${subtotalColumnWidth(table)}`,
  };
}

function hiddenHoursDecorations(doc: PMNode): DecorationSet {
  const decorations: Decoration[] = [];
  for (const table of investmentTables(doc)) {
    if (!table.showRowPrices) {
      decorations.push(
        Decoration.node(table.pos, table.pos + table.node.nodeSize, { class: HIDDEN_ROW_PRICES_CLASS })
      );
      continue;
    }
    if (!table.hourly) continue;
    if (!table.showHours)
      decorations.push(
        Decoration.node(table.pos, table.pos + table.node.nodeSize, tableAttributes(table.node))
      );
    table.node.forEach((row, offset) => {
      if (
        row.firstChild?.type.name === 'tableHeader' ||
        row.attrs.investmentProduct ||
        row.attrs.productPrice
      )
        return;
      if (investmentRowShowsHours(row.attrs, table.defaultShowHours)) return;
      const pos = table.pos + 1 + offset;
      decorations.push(Decoration.node(pos, pos + row.nodeSize, { class: PRICE_ONLY_CLASS }));
    });
  }
  return DecorationSet.create(doc, decorations);
}

/** TableKit does not consistently apply decoration attributes to its table DOM. */
function syncHiddenHoursDom(view: EditorView) {
  view.dom.querySelectorAll(`table.${HIDDEN_HOURS_CLASS}`).forEach((table) => {
    table.classList.remove(HIDDEN_HOURS_CLASS);
    (table as HTMLElement).style.removeProperty('--investment-subtotal-width');
  });
  view.dom
    .querySelectorAll(`table.${HIDDEN_ROW_PRICES_CLASS}`)
    .forEach((table) => table.classList.remove(HIDDEN_ROW_PRICES_CLASS));
  for (const table of investmentTables(view.state.doc)) {
    if (!table.hourly && table.showRowPrices) continue;
    const dom = view.nodeDOM(table.pos);
    const tableDom =
      dom instanceof HTMLTableElement
        ? dom
        : dom instanceof Element
          ? dom.querySelector('table')
          : null;
    if (!tableDom) continue;
    if (!table.showRowPrices) tableDom.classList.add(HIDDEN_ROW_PRICES_CLASS);
    else if (!table.showHours) {
      tableDom.classList.add(HIDDEN_HOURS_CLASS);
      tableDom.style.setProperty('--investment-subtotal-width', subtotalColumnWidth(table.node));
    }
  }
}

export const InvestmentHiddenHours = Extension.create({
  name: 'investmentHiddenHours',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        view: (view) => {
          syncHiddenHoursDom(view);
          return { update: (nextView) => syncHiddenHoursDom(nextView) };
        },
        props: { decorations: (state) => hiddenHoursDecorations(state.doc) },
      }),
    ];
  },
});
export default InvestmentHiddenHours;
